import React from "react";
import { toast } from "react-toastify";

const SecurityPage = ({ 
  kycViewData, 
  handleKycSubmit, 
  isKycLoading, 
  handleKycFileUpload,
  uploadedKycDocs, 
  kycFileNames, 
  passwordForm, 
  setPasswordForm, 
  handlePasswordChange, 
  isPasswordLoading, 
  handleGenerateHashkey, 
  hashTimer, 
  newHashkey 
}) => {
  const handleCopyKey = () => {
    navigator.clipboard.writeText(newHashkey);
    toast.success("API key copied");
  };

  // Helper to check if a URL is a PDF
  const isPdf = (url) => url && url.toLowerCase().includes('.pdf');

  // Check if both files are uploaded to enable the submit button
  const allDocsUploaded = uploadedKycDocs?.identification && uploadedKycDocs?.krapin;

  return (
    <div className="view-container">
      <div className="page-header">
        <h1>Compliance & System Security</h1>
      </div>

      <div className="panel-grid">
        {/* KYC SECTION */}
        <div className="card">
          <h2 className="card-title">KYC</h2>
          {kycViewData ? (
            <div>
              <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid var(--success)', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
                <p style={{ color: 'var(--success)', fontSize: '0.9rem', fontWeight: 500 }}>
                  <strong>Verification Complete:</strong> Your identity documents have been verified successfully.
                </p>
              </div>

              {/* Uploaded Documents Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                
                {/* ID Preview */}
                {kycViewData.identification && (
                  <div style={{ background: '#111827', padding: '1rem', borderRadius: '12px', border: '1px solid var(--border)' }}>
                    <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1rem', fontWeight: 600 }}>National Identification</span>
                    <a href={kycViewData.identification} target="_blank" rel="noopener noreferrer" style={{ display: 'block', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border)', textDecoration: 'none', background: '#000' }}>
                      {isPdf(kycViewData.identification) ? (
                        <iframe 
                          src={`${kycViewData.identification}#toolbar=0&navpanes=0&scrollbar=0`} 
                          title="ID Document" 
                          style={{ width: '100%', height: '160px', border: 'none', display: 'block', pointerEvents: 'none' }} 
                        />
                      ) : (
                        <img src={kycViewData.identification} alt="ID Document" style={{ width: '100%', height: '160px', objectFit: 'cover', display: 'block' }} />
                      )}
                    </a>
                  </div>
                )}

                {/* KRA PIN Preview */}
                {kycViewData.krapin && (
                  <div style={{ background: '#111827', padding: '1rem', borderRadius: '12px', border: '1px solid var(--border)' }}>
                    <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1rem', fontWeight: 600 }}>KRA PIN Document</span>
                    <a href={kycViewData.krapin} target="_blank" rel="noopener noreferrer" style={{ display: 'block', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border)', textDecoration: 'none', background: '#000' }}>
                      {isPdf(kycViewData.krapin) ? (
                        <iframe 
                          src={`${kycViewData.krapin}#toolbar=0&navpanes=0&scrollbar=0`} 
                          title="KRA Document" 
                          style={{ width: '100%', height: '160px', border: 'none', display: 'block', pointerEvents: 'none' }} 
                        />
                      ) : (
                        <img src={kycViewData.krapin} alt="KRA Document" style={{ width: '100%', height: '160px', objectFit: 'cover', display: 'block' }} />
                      )}
                    </a>
                  </div>
                )}
                
              </div>
            </div>
          ) : (
            <form onSubmit={handleKycSubmit}>
              <label className="form-label">National Identification</label>
              <input 
                type="file" 
                className="form-input" 
                accept="image/*, application/pdf" 
                onChange={e => handleKycFileUpload("identification", e.target.files[0])} 
              />
              {kycFileNames?.identification && (
                <div style={{ fontSize: '0.8rem', color: 'var(--success)', marginTop: '-8px', marginBottom: '1.25rem', fontWeight: 500 }}>
                  ✓ {kycFileNames.identification} uploaded successfully
                </div>
              )}
              
              <label className="form-label">KRA Pin</label>
              <input 
                type="file" 
                className="form-input" 
                accept="application/pdf, image/*" 
                onChange={e => handleKycFileUpload("krapin", e.target.files[0])} 
              />
              {kycFileNames?.krapin && (
                <div style={{ fontSize: '0.8rem', color: 'var(--success)', marginTop: '-8px', marginBottom: '1.25rem', fontWeight: 500 }}>
                  ✓ {kycFileNames.krapin} uploaded successfully
                </div>
              )}
              
              <button type="submit" className="btn-primary" disabled={isKycLoading || !allDocsUploaded}>
                {isKycLoading ? "Processing..." : "Submit KYC Documents"}
              </button>
            </form>
          )}
        </div>

        {/* ACCOUNT SECURITY SECTION */}
        <div className="card">
          <h2 className="card-title">Account Security</h2>
          
          <form onSubmit={handlePasswordChange} style={{ marginBottom: '2rem' }}>
            <label className="form-label">Old password</label>
            <input 
              type="password" 
              className="form-input" 
              required 
              value={passwordForm.oldPassword || ""} 
              onChange={e => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })} 
            />
            
            <label className="form-label">New password</label>
            <input 
              type="password" 
              className="form-input" 
              required 
              value={passwordForm.newPassword || ""} 
              onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} 
            />

            <label className="form-label">Confirm new password</label>
            <input 
              type="password" 
              className="form-input" 
              required 
              value={passwordForm.confirmPassword || ""} 
              onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} 
            />

            <button type="submit" className="btn-primary" disabled={isPasswordLoading}>
              {isPasswordLoading ? "Updating..." : "Update password"}
            </button>
          </form>
          

          <hr style={{ borderColor: 'var(--border)', margin: '2rem 0' }} />
          
          {/* API KEY SECTION */}
          <label className="form-label">API key</label>
          <button type="button" className="btn-primary" style={{ background: '#111827', border: '1px solid var(--border)', color: 'white' }} onClick={handleGenerateHashkey} disabled={hashTimer > 0}>
            Generate API key
          </button>
          
          {newHashkey && (
            <div style={{ marginTop: '1.5rem', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid var(--warning)', padding: '1.5rem', borderRadius: '12px' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--warning)', display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                <span>Security Notice: Key expires in:</span>
                <span>{hashTimer}s</span>
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '1.25rem', fontWeight: 700, color: 'white', margin: '1rem 0' }}>{newHashkey}</div>
              <button type="button" className="btn-primary" style={{ padding: '0.5rem', fontSize: '0.85rem' }} onClick={handleCopyKey}>Copy API Key</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecurityPage;